import { createAsyncThunk } from "@reduxjs/toolkit";
import { invalidateFreightResponse, updateSkipFreightCalculation } from "../../../checkout/shop-checkout";
import { fetchUserProfile } from "../../../user-profile/user-profile.thunks";
import store, { waitForCart, waitForUser } from "../../../../store/store";
import { getCart, getCartContext, getGuestCartToken, getItemId, getLoggedInCartQuoteId } from "../store/shop-cart.selectors";
import { NATIONAL_STORE, ShopCartUrlContext } from "../../../../common/utils/cart-util";
import { StoreState } from "../../../../store/store.interface";
import { AddProductMagentoResponse, ShopCart } from "../interfaces/shop-cart.interface";
import { isHeStorePurchaseFlow, isNationalFlow, isTunnelPurchaseFlow } from '../../../../common/utils/util';
import { getCartItemMagento, getTokenFromMagento } from "./shop-cart-commons";
import { addToGuestCartMagentoApiCall, addToLoggedInCartMagentoApiCall, updateGuestCartMagentoApiCall, updateLoggedInCartMagentoApiCall } from "../api/shop-cart.api";
import { getPromoCode } from "../../../promo-code/promo-code.selectors";
import { getZipCode } from "../../../estimated-tax/estimated-tax.selectors";
import { getEstimatedTax } from "../../../estimated-tax/estimated-tax.thunks";
import { getCustomerToken, isCustomerLoginSuccess } from "../../../customer/customer";
import { deletePromoCode } from "./delete-promo-code.thunks";

export const addToCart = createAsyncThunk('shopCart/addToCart', async (shopCartUrlContext: ShopCartUrlContext, thunkAPI) => {
    try {
      thunkAPI.dispatch(invalidateFreightResponse());
      thunkAPI.dispatch(updateSkipFreightCalculation(false));
      thunkAPI.dispatch(fetchUserProfile());
      await waitForUser();
      shopCartUrlContext.isLoggedIn = getCartContext(thunkAPI.getState() as StoreState).isLoggedIn;
      let cart: ShopCart;
      let response: AddProductMagentoResponse | undefined = undefined;

      if (!shopCartUrlContext.addProductMagentoRequest) {
        // [SHOP-API-REMOVAL][WECOMM-20796] Shop add-to-cart path is removed.
        // addToCartUrlContext should always build a Magento request now; if we hit
        // this branch, the request was constructed by an unknown caller still using
        // the old AddProductRequest (Shop) shape.
        console.error(
          '[SHOP-API-REMOVAL][WECOMM-20796] addToCart thunk: missing addProductMagentoRequest. ' +
          'The Shop /store/api/v1/checkout/.../cart/product endpoint has been removed. ' +
          'shopCartUrlContext=%o',
          shopCartUrlContext
        );
        throw new Error('addToCart: addProductMagentoRequest is required');
      }

      const state = thunkAPI.getState() as StoreState;

      const shopCart = getCart(state);
      const promoCode = getPromoCode(state);
      const forceUpdateGuestToken = isNationalFlow() && shopCart?.itemsCount !== 0 && shopCart?.storeName !== NATIONAL_STORE;
      await getTokenFromMagento(thunkAPI, forceUpdateGuestToken);
      const magentoState = thunkAPI.getState() as StoreState;
      response = await addToMagentoCart(shopCartUrlContext, magentoState);

      // If we have already found a promo code, remove it after the quantity changes
      if (promoCode) {
        await thunkAPI.dispatch(deletePromoCode({ forceUpdate: true, skipCalculation: true }));
      }

      cart = await getCartItemMagento(magentoState);

      return { ...cart, addToCartResponse: response, store: shopCartUrlContext.storeCode };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      const state = thunkAPI.getState() as StoreState;
  
      // If we have already found an estimated tax, update it after the quantity changes
      const zipCode = getZipCode(state);
      if (zipCode) {
        thunkAPI.dispatch(getEstimatedTax({ zipCode }));
      }
    }
  }, {
    condition: (async (_arg) => {
      await waitForCart();
      return true;
    })
  });

  async function addToMagentoCart(
    shopCartUrlContext: ShopCartUrlContext,
    state: StoreState
  ) {
    const itemId = getItemId(state, shopCartUrlContext.addProductMagentoRequest?.cartItem.sku);
    if (isCustomerLoginSuccess(state)) {
      const cartQuoteId = getLoggedInCartQuoteId(state);
      const customerToken = getCustomerToken(state);
      if (cartQuoteId) {
        if (itemId && !shopCartUrlContext.forceNewProduct) {
          return await updateLoggedInCartMagentoApiCall(
            itemId,
            shopCartUrlContext,
            cartQuoteId,
            customerToken
          );
        }

        if (
          shopCartUrlContext.storeCode &&
          shopCartUrlContext.addProductMagentoRequest &&
          !(isNationalFlow() || isTunnelPurchaseFlow() || isHeStorePurchaseFlow())
        ) {
          shopCartUrlContext.addProductMagentoRequest.cartItem.extension_attributes = {
            ...shopCartUrlContext.addProductMagentoRequest.cartItem.extension_attributes,
            custom_store_code: shopCartUrlContext.storeCode,
          };
        }
        
        return await addToLoggedInCartMagentoApiCall(
          shopCartUrlContext,
          cartQuoteId,
          customerToken
        );
      } else {
        throw new Error('quoteId not defined');
      }
    } else {
      const guestCartToken = getGuestCartToken(store.getState() as StoreState);
      if (guestCartToken) {
        if (itemId && !shopCartUrlContext.forceNewProduct) {
          return await updateGuestCartMagentoApiCall(
            itemId,
            shopCartUrlContext,
            guestCartToken
          );
        }
  
        if(shopCartUrlContext.storeCode && shopCartUrlContext.addProductMagentoRequest && !isNationalFlow() && !isHeStorePurchaseFlow())
          shopCartUrlContext.addProductMagentoRequest.cartItem.extension_attributes = {
            ...shopCartUrlContext.addProductMagentoRequest.cartItem.extension_attributes,
            custom_store_code: shopCartUrlContext.storeCode
          };
  
        return await addToGuestCartMagentoApiCall(
          shopCartUrlContext,
          guestCartToken
        );
      } else {
        throw new Error('magentoToken not defined');
      }
    }
  }
